import { useEffect, useMemo, useState } from "react";
import { Col, OverlayTrigger, Placeholder, Row, Toast, ToastContainer, Tooltip } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstLiveTable from "../../components/DstTable/DstLiveTable";
import DstTable from "../../components/DstTable/DstTable";
import DstTabs from "../../components/DstTabs/DstTabs";
import infoRow from "../../components/InfoRow/InfoRow";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import { useSession } from "../../contexts/SessionContext";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { EvaluationAttemptDetails } from "./ Evaluation";
import {
  fetchEvaluation,
  fetchEvaluationModules,
  handleCopy,
  navigateToEvaluationFilePreSignedUrl,
} from "./Evaluation.function";
import EvaluationInvitationModal from "./components/InvitationModal/InvitationModal";
import UpdateModal from "./components/UpdateModal/UpdateModal";

const Evaluation = () => {
  const { language } = useSession();
  const navigate = useNavigate();

  const { evaluation_id } = useParams<{ evaluation_id: string }>();
  const [evaluation, setEvaluation] = useState<any | null>(null);
  const [evaluationModules, setEvaluationModules] = useState<any>();
  const [loaded, setLoaded] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [activeTab, setActiveTab] = useState("general-info");
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!evaluation_id) return;
    fetchEvaluation(evaluation_id, setEvaluation, setLoaded, language);
    fetchEvaluationModules(Number(evaluation_id), setEvaluationModules, setLoaded, language);
  }, [evaluation_id, language]);

  const attemptsColumns = useMemo(
    () => [
      {
        name: translate(language, "PAGES.EVALUATION.ATTEMPTS.ID"),
        key: "id",
      },
      {
        name: translate(language, "PAGES.EVALUATION.ATTEMPTS.USER"),
        key: "user",
        render: (attempt: EvaluationAttemptDetails) => (
          <span
            className="clickable text-primary"
            onClick={(event) => completeNavigate(event, navigate, `/user/${attempt.user.id}`)}
          >
            {attempt.user.full_name}
          </span>
        ),
      },
      {
        name: translate(language, "PAGES.EVALUATION.ATTEMPTS.STATUS"),
        key: "status",
        render: (attempt: EvaluationAttemptDetails) => {
          const statusColorMap: { [key: string]: string } = {
            CREATED: "secondary",
            IN_PROGRESS: "warning",
            SUCCESS: "success",
            FAILED: "danger",
            BEING_CORRECTED: "info",
            PRE_CORRECTED: "primary",
            PRE_CORRECTION_IN_PROGRESS: "info",
            CORRECTION_PENDING: "warning",
          };
          return (
            <span className={`badge bg-${statusColorMap[attempt.status] || "secondary"}`}>
              {translate(language, `PAGES.EVALUATION.ATTEMPTS.STATUS_OPTIONS.${attempt.status}`) || attempt.status}
            </span>
          );
        },
        sortable: true,
      },
      {
        name: translate(language, "PAGES.EVALUATION.ATTEMPTS.START_DATE"),
        key: "dt_start",
        render: (attempt: EvaluationAttemptDetails) => (
          <span>{attempt.dt_start ? formatDateHumanly(language, attempt.dt_start, true) : "N/A"}</span>
        ),
        sortable: true,
      },
      {
        name: translate(language, "PAGES.EVALUATION.ATTEMPTS.END_DATE"),
        key: "dt_ended",
        render: (attempt: EvaluationAttemptDetails) => (
          <span>{attempt.dt_ended ? formatDateHumanly(language, attempt.dt_ended, true) : "N/A"}</span>
        ),
        sortable: true,
      },
      {
        name: translate(language, "PAGES.EVALUATION.ATTEMPTS.DEADLINE"),
        key: "deadline",
        render: (attempt: EvaluationAttemptDetails) => (
          <span>{attempt.deadline ? formatDateHumanly(language, attempt.deadline, true) : "N/A"}</span>
        ),
        sortable: true,
      },
    ],
    [language, navigate]
  );

  if (!loaded) {
    return (
      <div className="mx-3 mt-4">
        <Placeholder as="div" animation="glow">
          <Placeholder xs={6} className="mb-3" /> <br />
          <Placeholder xs={4} className="mb-4" />
        </Placeholder>
        <Row>
          <Col md={6}>
            {[...Array(6)].map((_, i) => (
              <Placeholder
                as="div"
                animation="glow"
                className="border-bottom mb-3 pb-2 d-flex justify-content-between align-items-center"
                key={`col1-${i}`}
              >
                <Placeholder xs={8} />
                <Placeholder xs={3} />
              </Placeholder>
            ))}
          </Col>
          <Col md={6}>
            {[...Array(6)].map((_, i) => (
              <Placeholder
                as="div"
                animation="glow"
                className="border-bottom mb-3 pb-2 d-flex justify-content-between align-items-center"
                key={`col2-${i}`}
              >
                <Placeholder xs={7} />
                <Placeholder xs={4} />
              </Placeholder>
            ))}
          </Col>
        </Row>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="alert alert-danger m-4" role="alert">
        {translate(language, "PAGES.EVALUATION.ERROR_LOADING")}
      </div>
    );
  }

  const generalInformationTab = (
    <Row>
      <Col md={6}>
        {infoRow(
          "dst-icon-hash-01",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_ID"),
          evaluation.id,
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="copy-tooltip">{translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.COPY_ID")}</Tooltip>
            }
          >
            <span
              className="dst-icon-copy-01 clickable"
              onClick={() => handleCopy(evaluation.id, () => setShowCopyToast(true))}
            />
          </OverlayTrigger>
        )}
        {infoRow(
          "dst-icon-file-code-01",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_TYPE"),
          evaluation.type
        )}
        {infoRow(
          "dst-icon-code-01",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_LANGUAGE_PROGRAMMING"),
          evaluation.programming_language
        )}
        {infoRow(
          "dst-icon-clock",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_DURATION"),
          `${evaluation.duration} ${translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.MINUTES")}`
        )}
        {infoRow(
          "dst-icon-globe-01",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_LANGUAGE"),
          evaluation.language
        )}
        {infoRow(
          "dst-icon-refresh-ccw-02",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_ALLOW_REPASS"),
          evaluation.allow_repass
            ? translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.YES")
            : translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.NO")
        )}
        {infoRow(
          "dst-icon-alert-triangle",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_IS_DEPRECATED"),
          evaluation.is_deprecated
            ? translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.YES")
            : translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.NO")
        )}
      </Col>

      <Col md={6}>
        {infoRow(
          "dst-icon-check-verified-01",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_CORRECTION_MODULE"),
          evaluation.correction_module?.name ?? "N/A"
        )}
        {infoRow(
          "dst-icon-layers-three-01",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_PROGRAM"),
          evaluation.program?.name ?? "N/A"
        )}
        {infoRow(
          "dst-icon-database-01",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_S3_NAME"),
          evaluation.s3_name,
          <span
            className="blue-font dst-icon-download-01 clickable"
            onClick={() => navigateToEvaluationFilePreSignedUrl(evaluation.id, language)}
          />
        )}
        {infoRow(
          "dst-icon-user-01",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_OWNER"),
          evaluation.owner ? (
            <Link to={`/user/${evaluation.owner.id}`} className="text-decoration-none">
              {`${evaluation.owner.first_name} ${evaluation.owner.last_name}`}
            </Link>
          ) : (
            "N/A"
          )
        )}
        {infoRow(
          "dst-icon-bar-chart-01",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_SUCCESS_RATIO"),
          <div className="d-flex align-items-center">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="success-ratio-tooltip">
                  {translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.EXPLAIN_SUCCESS_RATIO")}
                </Tooltip>
              }
            >
              <span className="dst-icon-info-circle clickable me-2" />
            </OverlayTrigger>
            {evaluation.success_ratio !== null ? `${(evaluation.success_ratio * 100).toFixed(0)}%` : "N/A"}
          </div>
        )}
        {infoRow(
          "dst-icon-calendar-plus-02",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_CREATED_AT"),
          formatDateHumanly(language, evaluation.dt_created)
        )}
        {infoRow(
          "dst-icon-calendar-check-02",
          translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.FIELD_UPDATED_AT"),
          evaluation.dt_updated ? formatDateHumanly(language, evaluation.dt_updated) : "N/A"
        )}
      </Col>
    </Row>
  );

  const columns = [
    { name: translate(language, "PAGES.EVALUATION.RELATED_MODULES.ID"), key: "id" },
    { name: translate(language, "PAGES.EVALUATION.RELATED_MODULES.NAME"), key: "name" },
    { name: translate(language, "PAGES.EVALUATION.RELATED_MODULES.DESCRIPTION"), key: "description" },
    { name: translate(language, "PAGES.EVALUATION.RELATED_MODULES.PROGRAMMING_LANGUAGE"), key: "programming_language" },
    { name: translate(language, "PAGES.EVALUATION.RELATED_MODULES.LANGUAGE"), key: "language" },
    { name: translate(language, "PAGES.EVALUATION.RELATED_MODULES.PURPOSE"), key: "purpose" },
    {
      name: translate(language, "PAGES.EVALUATION.RELATED_MODULES.OWNER"),
      key: "owner",
      render: (module: any) => (
        <span
          className="clickable text-primary"
          onClick={(event) => completeNavigate(event, navigate, `/user/${module.owner.id}`)}
        >
          {module.owner.first_name} {module.owner.last_name}
        </span>
      ),
    },
    {
      name: translate(language, "PAGES.EVALUATION.RELATED_MODULES.STATUS"),
      key: "is_deprecated",
      render: (module: any) =>
        module.is_deprecated ? (
          <span className="badge bg-secondary">
            {translate(language, "PAGES.EVALUATION.RELATED_MODULES.STATUS_OPTIONS.DEPRECATED")}
          </span>
        ) : (
          <span className="badge bg-success">
            {translate(language, "PAGES.EVALUATION.RELATED_MODULES.STATUS_OPTIONS.ACTIVE")}
          </span>
        ),
    },
    {
      name: translate(language, "PAGES.EVALUATION.RELATED_MODULES.CREATED_AT"),
      key: "created_at",
      render: (module: any) => <span>{formatDateHumanly(language, new Date(module.dt_created).toISOString())}</span>,
    },
    {
      name: translate(language, "PAGES.EVALUATION.RELATED_MODULES.UPDATED_AT"),
      key: "updated_at",
      render: (module: any) => (
        <span>
          {module.updated_at ? formatDateHumanly(language, new Date(module.dt_updated).toISOString()) : "N/A"}
        </span>
      ),
    },
  ];

  const modulesTab = (
    <div className="mt-3">
      <DstTable
        data={evaluationModules || []}
        striped={false}
        columns={columns}
        onRowClick={(event, item) => completeNavigate(event, navigate, `/module/${item.id}`)}
      />
    </div>
  );

  const attemptsTab = (
    <div className="mt-3">
      <div className="d-flex justify-content-start mb-3">
        <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
      </div>
      <DstLiveTable
        classTable="text-nowrap"
        remote={`/evaluations/${evaluation_id}/attempts${searchQuery ? `?search=${searchQuery}` : ""}`}
        remoteKey="evaluation_attempts"
        columns={attemptsColumns}
        pagination
        paginationAlwaysShowFooter
        itemsPerPage={20}
        paginationResizeOptions={[10, 50, 100]}
        striped
        onRowClick={(event, item) => completeNavigate(event, navigate, `/evaluation-attempt/${item.id}`)}
      />
    </div>
  );

  const tabsData = [
    {
      id: "general-info",
      title: translate(language, "PAGES.EVALUATION.GENERAL_INFORMATION.TITLE"),
      content: generalInformationTab,
    },
    {
      id: "module-info",
      title: translate(language, "PAGES.EVALUATION.RELATED_MODULES.TITLE"),
      content: modulesTab,
    },
    {
      id: "attempts-info",
      title: translate(language, "PAGES.EVALUATION.ATTEMPTS.TITLE"),
      content: attemptsTab,
    },
  ];

  return (
    <div className="mx-4 mt-4">
      <Helmet>
        <title>Evaluation - {evaluation.name}</title>
      </Helmet>
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex">
            <span
              className="dst-icon-arrow-circle-left h2 me-2 mt-1 mb-0 blue-font clickable"
              onClick={() => navigate("/evaluations")}
              role="button"
              aria-label="Go to Evaluations page"
            />
            <div>
              <h1 className="fw-bold">{evaluation.name}</h1>
              <p className="text-muted">{evaluation.description || "-"}</p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <DstButton
              variant="light"
              value={translate(language, "PAGES.EVALUATION.BUTTONS.UPDATE_EVALUATION")}
              btnImageBefore={<span className="me-2 dst-icon-edit-03" />}
              clickFunction={() => setShowUpdateModal(true)}
            />
            <DstButton
              value={translate(language, "PAGES.EVALUATION.BUTTONS.CREATE_EVALUATION_INVITATION")}
              btnImageBefore={<span className="me-2 dst-icon-plus" />}
              clickFunction={() => setShowInvitationModal(true)}
            />
          </div>
        </div>
        <UpdateModal
          show={showUpdateModal}
          closeModal={() => setShowUpdateModal(false)}
          evaluation={evaluation}
          language={language}
          onSuccess={() => fetchEvaluation(evaluation_id!, setEvaluation, setLoaded, language)}
        />
        <EvaluationInvitationModal
          closeModal={() => setShowInvitationModal(false)}
          language={language}
          show={showInvitationModal}
          evaluationId={evaluation_id!}
        />
      </div>

      <DstTabs
        tabsData={tabsData}
        variant="primary"
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key || "general-info")}
      />

      <ToastContainer position="top-end" className="p-3">
        <Toast bg="success" show={showCopyToast} onClose={() => setShowCopyToast(false)} delay={1500} autohide>
          <Toast.Body className="text-white">{translate(language, "PAGES.EVALUATION.SUCCESS_COPY")}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Evaluation;
